import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Hospital Operations & Management) ---
import {
    // Core Concepts & General
    faNetworkWired,faClock,
    faTasks,            // Operations / Management / Efficiency
    faHospital,         // Hospital Facility
    faUserTie,          // Consulting / Expertise / Leadership / Operator
    faCheckCircle,      // Quality / Compliance / Accreditation / Success
    faDollarSign,       // Profitability / Financial Health / ROI
    faUsers,            // Patient Experience / Staff / Community
    faLightbulb,        // Strategy / Innovation
    // Core Services
    // faTasks reused for Operations Management
    faBullhorn,         // Marketing & Branding
    faAward,            // Accreditation & Compliance ( NABL, JCI)
    faChalkboardTeacher,// Manpower Training
    // Digital Transformation
    faLaptopMedical,    // HIMS / HIS / EHR / EMR / Health Tech
    faCloud,            // Cloud Data Management
    faBrain,            // AI Analytics / Decision Support
    faMobileAlt,        // Virtual OPD / Telemedicine / Apps / Portals
    faShieldAlt,        // IT Compliance / Cyber-Security
    // Patient Experience
    faSmileBeam,        // Patient Satisfaction / Experience
    faRoute,            // Patient Journey / Flow
    faConciergeBell,    // Service Excellence Desk / Hospitality
    faComments,         // Feedback / Communication / Queries
    // Commitment / Why Choose Us
    faHandshake,        // Partnership / Commitment
    faChartLine,        // Growth / Performance / ROI-focused
    faUsersCog,         // Dedicated Team / Experts
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faProjectDiagram, faMapSigns, faBullseye, faSitemap, faCalculator, faGavel,
    faHandHoldingDollar, faExpandArrowsAlt, faTrophy, faThumbsUp, faBuilding, faMedal,
    faBoxesStacked, faWarehouse, faClipboardList, faHeartPulse, faBolt, faDatabase,
    faSearch, faBalanceScale, faChartBar, faCalendarAlt, faShoppingCart, faMicroscope,
    faTools, faCog, faWeightHanging, faWrench, faNetworkWired, faLifeRing, faUserCheck,
    faGlobe, faLeaf, faBiohazard, faPalette, faDirections, faRulerCombined
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Management Matters
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Core Services
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Digital Transformation
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Patient Experience
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // Cliniqor's Commitment
    const [title6Ref, title6Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for operations/management
    const heroBgImage = 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Business/collaboration image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Hospital Operations & Management Consulting</h1>
            <p className="cliniqor-sub-heading">
              Powered by Cliniqor – Healthcare Excellence Delivered
            </p>
            <p className="cliniqor-intro-text">
              Effective operation and management are key to sustainability, profitability, and quality care. Cliniqor offers end-to-end consulting, implementation, and support for new and existing hospitals.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- WHY MANAGEMENT MATTERS --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>Why Hospital Management Matters</h2>
              <p>Beyond building: running profitably, compliantly, and efficiently.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>The success of your healthcare investment depends on daily management amidst evolving patient expectations, regulations, and costs. A seasoned partner like Cliniqor is crucial.</p>
                     <p>We empower leadership with world-class systems focusing on lean operations, quality improvement, accreditation readiness, workforce training, and profitability planning.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- CORE SERVICES (Detailed Planning Section - 4 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Core Services in Hospital Operations & Management</h2>
               <p>Comprehensive support across key operational domains.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 4 Core Services */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTasks} /></div>
                    <div className="cliniqor-content"><h4>Hospital Operations Management</h4> <p>Turnkey/modular support for daily functioning, workflow streamlining, relationship development, patient services, manpower, TPA, inventory, billing, SOPs, quality assurance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBullhorn} /></div>
                    <div className="cliniqor-content"><h4>Hospital Marketing & Branding Management</h4> <p>Strategic/on-ground support for identity, outreach, GP engagement, events, digital marketing, partnerships, campaigns, budgeting, ROI tracking.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faAward} /></div>
                    <div className="cliniqor-content"><h4>Accreditation & Compliance Readiness</h4> <p>Preparation for  NABL, JCI including internal audits, SOP alignment, training, mock assessments, compliance tracking.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                    <div className="cliniqor-content"><h4>Manpower Training & Induction Programs</h4> <p>Skill-building, orientation, customer service, upskilling, SOP familiarization, leadership grooming, CPD planning.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- DIGITAL TRANSFORMATION (Features Section) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Digital Transformation & Health Tech Integration</h2>
                 <p>Redefining Healthcare Operations Through Technology – Powered by Cliniqor</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginBottom: '30px' }}>
                   <p>Stay ahead by implementing smart systems. Cliniqor helps modernize infrastructure for automation, better decision-making, and enhanced patient journeys.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Tech Services --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptopMedical} /></div>
                            <h4>HIMS/HIS & EMR/EHR</h4>
                            <p>Selection, setup, implementation, and training.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faNetworkWired} /></div> {/* Integration */}
                            <h4>RIS, LIS, PACS Integration</h4>
                            <p>Connecting key diagnostic and imaging systems.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCloud} /></div>
                            <h4>Cloud Data Management</h4>
                            <p>Secure, scalable data storage and multi-center access.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBrain} /></div>
                            <h4>AI & Analytics Tools</h4>
                            <p>Powered analytics and clinical decision support.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faMobileAlt} /></div>
                            <h4>Virtual Care & Patient Tools</h4>
                            <p>Telemedicine systems, apps, kiosks, portals.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faShieldAlt} /></div>
                            <h4>IT Compliance & Security</h4>
                            <p>HIPAA-ready planning and cybersecurity measures.</p>
                        </div>
                    </div>
               </div>
                <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginTop: '30px' }}>
                   <p>Digitizing operations gains efficiency, reduces redundancy, unlocks new revenue streams, and delivers smart, scalable healthcare.</p>
              </div>
           </div>
        </section>

        {/* --- PATIENT EXPERIENCE (Detailed Planning Section - Reused) --- */}
         <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Use texture bg */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Patient Experience & Customer Service Management</h2>
                 <p>Creating Patient-Centered Hospitals That People Trust – With Cliniqor</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginBottom: '30px' }}>
                   <p>Success is defined by outcomes and interaction quality. Cliniqor designs systems placing patients at the heart of operations.</p>
              </div>
               {/* Using Planning Items for Patient Experience Focus Areas */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faRoute} /></div>
                    <div className="cliniqor-content"><h4>End-to-End Patient Journey Design</h4> <p>Optimizing workflows for seamless patient movement and interaction.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faConciergeBell} /></div>
                    <div className="cliniqor-content"><h4>Service Excellence Desks</h4> <p>Dedicated support for VIP and walk-in patients enhancing hospitality.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSmileBeam} /></div> {/* Satisfaction */}
                    <div className="cliniqor-content"><h4>Patient Satisfaction Measurement</h4> <p>Using NPS & feedback systems to measure and improve experiences.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faComments} /></div>
                    <div className="cliniqor-content"><h4>Staff Training & Communication</h4> <p>Focusing on empathy, communication, and service recovery skills.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClock} /></div> {/* Wait times */}
                    <div className="cliniqor-content"><h4>Reducing Wait Times & Bottlenecks</h4> <p>Streamlining processes to improve patient flow and reduce delays.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faCheckCircle} /></div> {/* Discharge */}
                    <div className="cliniqor-content"><h4>Discharge & Follow-Up Enhancement</h4> <p>Improving the final stages of care and post-discharge engagement.</p> </div>
                 </div>
                 {/* Can add more items for Special Programs, Complaint Redressal, Wow Moments if needed */}
            </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', marginTop: '30px' }}>
                   <p>Memorable patient experiences lead to higher retention, more referrals, and a strong emotional brand connect.</p>
              </div>
           </div>
        </section>


       {/* --- CLINIQOR'S COMMITMENT (Operator Role - Simple Text Section) --- */}
        <section className="cliniqor-section cliniqor-section-bg-light"> {/* Light background */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Cliniqor’s Commitment as a Hospital Operator</h2>
                 <p>Taking responsibility for end-to-end administration and performance.</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Our seasoned leadership team ensures day-to-day operational efficiency, strategic growth and financial health, brand building and market leadership, optimal staff performance, sustained quality and compliance, and a culture of accountability and excellence.</p>
                   <p>Hospitals under Cliniqor's operational umbrella benefit from our larger healthcare ecosystem, including central procurement, marketing, training, and benchmarking.</p>
              </div>
           </div>
        </section>

       {/* --- WHY CHOOSE CLINIQOR? (Features Layout - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section"> {/* Default Background */}
           <div className="cliniqor-container">
              <div ref={title6Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title6Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Why Choose Cliniqor?</h2>
                 <p>Our distinct advantages in hospital operations and management.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Why Choose --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                            <h4>Proven Experience</h4>
                            <p>Managing hospitals across India with deep workflow understanding.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                            <h4>Cost Optimization</h4>
                            <p>Lean operations expertise trimming inefficiencies without compromising quality.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div>
                            <h4>ROI-Focused Strategies</h4>
                            <p>Aligning operations with business goals for measurable financial results.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLightbulb} /></div>
                            <h4>Continuous Improvement Culture</h4>
                            <p>Embracing technology and innovation for ongoing enhancement.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsersCog} /></div>
                            <h4>Dedicated Expert Team</h4>
                            <p>Specialists in operations, clinical management, and marketing.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Let’s Make Your Hospital the Best-Run Facility in the Region</h2>
          <p>Whether planning new or streamlining existing, Cliniqor ensures peak performance. With proven frameworks and experienced professionals, we deliver clinical excellence, operational precision, and financial success. Great hospitals are managed to perfection.</p>
          <a href="#contact" className="cliniqor-cta-button">Partner with Cliniqor</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;